import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import BackToAdminLink from "@/components/BackToAdminLink";
import {
  VideoCamera,
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  X,
  ArrowClockwise,
  Users,
} from "@phosphor-icons/react";

// Iter35d — Admin Events CRUD.

const TYPES = [
  { key: "weekly", label: "Weekly ritual (Monday check-in)" },
  { key: "money-date", label: "Money Date (monthly 60min)" },
  { key: "one-off", label: "One-off (guest, workshop, training, etc.)" },
];

const EMPTY_FORM = {
  title: "",
  description: "",
  starts_at: "",
  duration_minutes: 30,
  zoom_url: "",
  kind: "weekly",
  recording_url: "",
  archived: false,
  // Iter38 — recurrence helper. When enabled on a new event, the backend
  // materialises `recurrence_count` sibling events (same series_id) spaced
  // weekly or monthly starting from `starts_at`.
  recurrence_enabled: false,
  recurrence_frequency: "weekly",
  recurrence_count: 8,
};

function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDatetimeInput(local) {
  if (!local) return "";
  // Interpret as local time, convert to ISO with timezone
  return new Date(local).toISOString();
}

// Iter38b — client-side preview of the recurrence dates the backend will
// generate. Mirrors `_expand_recurrence` in routes/events.py so admin sees
// exactly what will be saved (especially useful for the monthly "first
// Friday" pattern, which was previously invisible until after save).
function previewRecurrenceDates(startLocalStr, frequency, count) {
  if (!startLocalStr || !count || count < 2) return [];
  const first = new Date(startLocalStr);
  if (isNaN(first.getTime())) return [];
  const n = Math.min(Math.max(Number(count) || 2, 2), 26);
  const out = [];
  if (frequency === "weekly") {
    for (let i = 0; i < n; i++) {
      const d = new Date(first);
      d.setDate(d.getDate() + 7 * i);
      out.push(d);
    }
    return out;
  }
  // Monthly — Nth weekday of month pattern (matches backend).
  const weekday = first.getDay(); // JS: Sun=0..Sat=6
  const jsWeekday = weekday === 0 ? 6 : weekday - 1; // Convert to Mon=0..Sun=6
  const nth = Math.min(Math.floor((first.getDate() - 1) / 7) + 1, 4);
  let y = first.getFullYear();
  let m = first.getMonth();
  for (let i = 0; i < n; i++) {
    // Find Nth weekday of year y, month m.
    const firstOfMonth = new Date(y, m, 1, first.getHours(), first.getMinutes());
    const fomJsWeekday = firstOfMonth.getDay() === 0 ? 6 : firstOfMonth.getDay() - 1;
    const offset = (jsWeekday - fomJsWeekday + 7) % 7;
    let day = 1 + offset + 7 * (nth - 1);
    // Clamp to month length.
    const lastDay = new Date(y, m + 1, 0).getDate();
    if (day > lastDay) day -= 7;
    const candidate = new Date(y, m, day, first.getHours(), first.getMinutes());
    out.push(candidate);
    m += 1;
    if (m === 12) { m = 0; y += 1; }
  }
  return out;
}

export default function AdminEventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, ...form} or null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/events`);
      setEvents(res.data.events || []);
    } catch (e) {
      toast.error("Could not load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const startNew = () => setEditing({ isNew: true, ...EMPTY_FORM });
  const startEdit = (e) => setEditing({
    id: e.id,
    title: e.title || "",
    description: e.description || "",
    starts_at_local: toLocalDatetimeInput(e.starts_at),
    duration_minutes: e.duration_minutes || 30,
    zoom_url: e.zoom_url || "",
    kind: e.kind || "weekly",
    recording_url: e.recording_url || "",
    archived: !!e.archived,
  });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const payload = {
      title: editing.title,
      description: editing.description,
      starts_at: editing.isNew
        ? fromLocalDatetimeInput(editing.starts_at)
        : fromLocalDatetimeInput(editing.starts_at_local),
      duration_minutes: Number(editing.duration_minutes) || 30,
      zoom_url: editing.zoom_url,
      kind: editing.kind,
      recording_url: editing.recording_url || null,
      archived: !!editing.archived,
    };
    if (!payload.title || payload.title.length < 3) return toast.error("Title needed (3+ chars)");
    if (!payload.starts_at) return toast.error("Start time needed");
    if (!payload.zoom_url || payload.zoom_url.length < 8) return toast.error("Zoom URL needed");
    // Iter38 — only send recurrence on NEW events; editing an existing event
    // never re-expands the series (users edit occurrences individually).
    if (editing.isNew && editing.recurrence_enabled) {
      const count = Number(editing.recurrence_count) || 2;
      if (count < 2 || count > 26) {
        return toast.error("Repeat count must be between 2 and 26");
      }
      payload.recurrence = {
        frequency: editing.recurrence_frequency || "weekly",
        count,
      };
    }

    setSaving(true);
    try {
      if (editing.isNew) {
        const res = await axios.post(`${API}/admin/events`, payload);
        if (res.data?.count > 1) {
          toast.success(`Created ${res.data.count} events in this series`);
        } else {
          toast.success("Event created");
        }
      } else {
        await axios.patch(`${API}/admin/events/${editing.id}`, payload);
        toast.success("Event updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/events/${e.id}`);
      toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error("Could not delete event");
    }
  };

  const removeSeries = async (e) => {
    if (!e.series_id) return;
    if (!window.confirm(
      `Delete every FUTURE event in this recurring series?\n\n` +
      `Past events (with attendees / recordings) will be kept. This cannot be undone.`,
    )) return;
    try {
      const res = await axios.delete(`${API}/admin/events/series/${e.series_id}`);
      toast.success(`${res.data.deleted} future event${res.data.deleted === 1 ? "" : "s"} deleted from the series`);
      load();
    } catch (err) {
      toast.error("Could not delete series");
    }
  };

  const toggleArchive = async (e) => {
    try {
      await axios.patch(`${API}/admin/events/${e.id}`, { archived: !e.archived });
      toast.success(e.archived ? "Event unarchived" : "Event archived");
      load();
    } catch (err) {
      toast.error("Could not update archive status");
    }
  };

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-events-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <BackToAdminLink />
          <div className="section-eyebrow flex items-center gap-2">
            <VideoCamera size={16} weight="bold" /> Admin · Community
          </div>
          <h1 className="font-heading uppercase text-4xl mt-2">Events</h1>
          <p className="text-black/75 mt-3 leading-relaxed">
            Manage every community session — weekly Monday check-ins, monthly Money Dates, one-off
            guest workshops. Each event needs a title, start time (your local timezone —
            we convert to ISO for you), Zoom URL, and kind. After a Money Date, come back and paste
            the unlisted YouTube or Loom URL into <strong>Recording URL</strong> to make it appear in
            the public Replay library. Toggle <strong>Archive</strong> to hide an event without
            deleting it.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-primary" onClick={startNew} data-testid="admin-event-add-btn">
            <Plus size={14} weight="bold" /> Add event
          </button>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading} data-testid="admin-event-reload">
            <ArrowClockwise size={14} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className="card border-2 border-black" data-testid="admin-event-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-heading uppercase text-xl">
              {editing.isNew ? "New event" : "Edit event"}
            </h2>
            <button type="button" onClick={cancel} className="text-black/60 hover:text-black"
              data-testid="admin-event-editor-close">
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1">Title</label>
              <input type="text" className="input" value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="e.g. Monday morning check-in · 24 Aug"
                maxLength={140} autoFocus data-testid="admin-event-title" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Type</label>
              <select className="input" value={editing.kind}
                onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                data-testid="admin-event-kind">
                {TYPES.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Duration (minutes)</label>
              <input type="number" className="input" value={editing.duration_minutes}
                onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })}
                min={15} max={240} data-testid="admin-event-duration" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Start (your local time)</label>
              <input type="datetime-local" className="input"
                value={editing.isNew ? (editing.starts_at || "") : (editing.starts_at_local || "")}
                onChange={(e) => setEditing({ ...editing, [editing.isNew ? "starts_at" : "starts_at_local"]: e.target.value })}
                data-testid="admin-event-starts-at" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Zoom URL</label>
              <input type="url" className="input" value={editing.zoom_url}
                onChange={(e) => setEditing({ ...editing, zoom_url: e.target.value })}
                placeholder="https://zoom.us/j/..." data-testid="admin-event-zoom-url" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1">
                Recording URL <span className="text-black/50 font-normal">· optional, added after event</span>
              </label>
              <input type="url" className="input" value={editing.recording_url || ""}
                onChange={(e) => setEditing({ ...editing, recording_url: e.target.value })}
                placeholder="https://youtu.be/... (unlisted)" data-testid="admin-event-recording-url" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1">Description</label>
              <textarea className="input min-h-[80px]" value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                maxLength={4000} placeholder="What's this session about? Any pre-work?"
                data-testid="admin-event-description" />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!editing.archived}
                  onChange={(e) => setEditing({ ...editing, archived: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="admin-event-archived"
                />
                <span>
                  <strong>Archived</strong> — hide from the public /community page (both upcoming
                  and replays) without deleting.
                </span>
              </label>
            </div>
            {/* Iter38 — recurrence helper (create-only). Ticking the box
                materialises N future occurrences server-side so the admin
                doesn't have to keep coming back every Monday. */}
            {editing.isNew && (
              <div
                className="md:col-span-2 p-3 rounded-xl border-[1.5px] border-black/25 bg-[#FFF7E5]/40"
                data-testid="admin-event-recurrence"
              >
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!editing.recurrence_enabled}
                    onChange={(e) =>
                      setEditing({ ...editing, recurrence_enabled: e.target.checked })
                    }
                    className="w-4 h-4"
                    data-testid="admin-event-recurrence-toggle"
                  />
                  <span>
                    <strong>Repeat this event</strong> — create a whole series in one go so you
                    don't have to keep adding the same session every week.
                  </span>
                </label>
                {editing.recurrence_enabled && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-3 pl-6">
                    <div>
                      <label className="block text-xs font-semibold mb-1">Frequency</label>
                      <select
                        className="input"
                        value={editing.recurrence_frequency}
                        onChange={(e) =>
                          setEditing({ ...editing, recurrence_frequency: e.target.value })
                        }
                        data-testid="admin-event-recurrence-frequency"
                      >
                        <option value="weekly">Every week (same day &amp; time)</option>
                        <option value="monthly">Every month (same weekday, e.g. "first Friday")</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1">
                        How many occurrences? <span className="text-black/50 font-normal">(2–26)</span>
                      </label>
                      <input
                        type="number"
                        className="input"
                        min={2}
                        max={26}
                        value={editing.recurrence_count}
                        onChange={(e) =>
                          setEditing({ ...editing, recurrence_count: e.target.value })
                        }
                        data-testid="admin-event-recurrence-count"
                      />
                    </div>
                    {/* Iter38b — live preview of exact dates that will be
                        created. Especially useful for the monthly "first
                        Friday" pattern which was previously invisible
                        until save. */}
                    {(() => {
                      const dates = previewRecurrenceDates(
                        editing.starts_at,
                        editing.recurrence_frequency,
                        editing.recurrence_count,
                      );
                      if (!dates.length) {
                        return (
                          <p className="sm:col-span-2 text-xs text-black/60 italic">
                            Pick a start date above and we'll show you exactly which dates get created.
                          </p>
                        );
                      }
                      return (
                        <div className="sm:col-span-2" data-testid="admin-event-recurrence-preview">
                          <div className="text-xs font-semibold mb-2">
                            Preview — {dates.length} event{dates.length === 1 ? "" : "s"} will be created:
                          </div>
                          <ul className="text-xs text-black/75 leading-relaxed space-y-1 max-h-40 overflow-y-auto pr-2 border-l-2 border-black/20 pl-3">
                            {dates.map((d, i) => (
                              <li key={i}>
                                <span className="text-black/50 font-mono">#{i + 1}</span>{" "}
                                {d.toLocaleDateString("en-GB", {
                                  weekday: "short",
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                                {" · "}
                                {d.toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                    <p className="sm:col-span-2 text-xs text-black/60 leading-relaxed">
                      Each occurrence becomes its own event so you can edit or archive individual
                      sessions later. Use <strong>Delete series</strong> on any row to remove the
                      remaining future events in one go.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mt-5 flex gap-3">
            <button type="button" className="btn-primary" onClick={save} disabled={saving}
              data-testid="admin-event-save">
              <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save event"}
            </button>
            <button type="button" className="btn-secondary" onClick={cancel} data-testid="admin-event-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3" data-testid="admin-events-list">
        {loading && events.length === 0 ? (
          <p className="text-center text-black/55 py-8">Loading…</p>
        ) : events.length === 0 ? (
          <div className="card text-center">
            <p className="text-black/65">No events yet. Click "Add event" to create the first one.</p>
          </div>
        ) : (
          events.map((e) => {
            const startedAt = e.starts_at ? new Date(e.starts_at) : null;
            const isPast = startedAt && startedAt < new Date();
            return (
              <div key={e.id} className={`card !p-4 ${e.archived ? "opacity-50" : ""}`}
                data-testid={`admin-event-row-${e.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{e.title}</h3>
                      <span className="text-[10px] uppercase tracking-widest border border-black/30 rounded px-2 py-0.5">
                        {e.kind}
                      </span>
                      {isPast && <span className="text-[10px] uppercase tracking-widest bg-black/10 rounded px-2 py-0.5">Past</span>}
                      {e.archived && <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-800 rounded px-2 py-0.5">Archived</span>}
                      {e.recording_url && <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 rounded px-2 py-0.5">Replay live</span>}
                      {e.series_id && (
                        <span
                          className="text-[10px] uppercase tracking-widest bg-[#FFBD4A] text-black rounded px-2 py-0.5 font-semibold"
                          title={`Part of a recurring series · #${(e.series_index ?? 0) + 1}`}
                          data-testid={`admin-event-series-chip-${e.id}`}
                        >
                          🔁 Series #{(e.series_index ?? 0) + 1}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-black/60 mt-1">
                      {startedAt?.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} · {e.duration_minutes} min
                      {e.rsvp_ids?.length > 0 && (
                        <> · <span className="inline-flex items-center gap-1"><Users size={10} weight="bold" /> {e.rsvp_ids.length} RSVP</span></>
                      )}
                    </p>
                    {e.description && <p className="text-xs text-black/55 mt-1 line-clamp-2">{e.description}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button type="button" onClick={() => startEdit(e)}
                      className="btn-secondary !px-2 !py-1 !text-xs" data-testid={`admin-event-edit-${e.id}`}>
                      <PencilSimple size={12} weight="bold" /> Edit
                    </button>
                    <button type="button" onClick={() => toggleArchive(e)}
                      className="btn-secondary !px-2 !py-1 !text-xs" data-testid={`admin-event-archive-${e.id}`}>
                      {e.archived ? "Unarchive" : "Archive"}
                    </button>
                    <button type="button" onClick={() => remove(e)}
                      className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-red-600 text-red-700 hover:bg-red-50 font-semibold inline-flex items-center gap-1"
                      data-testid={`admin-event-delete-${e.id}`}>
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                    {e.series_id && !isPast && (
                      <button
                        type="button"
                        onClick={() => removeSeries(e)}
                        className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-red-800 text-red-800 hover:bg-red-100 font-semibold inline-flex items-center gap-1"
                        title="Delete every FUTURE event in this recurring series"
                        data-testid={`admin-event-delete-series-${e.id}`}
                      >
                        <Trash size={12} weight="bold" /> Delete series
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
